import {SxProps, Theme} from '@mui/material';
import theme from 'src/theme';

/**
 * The phone design language, as drawn on the design canvas.
 *
 * One file so that a card in a list, a record's page and a sheet of actions cannot drift apart: every
 * screen below a desktop is built from these. The values are the canvas's, expressed in the app's own
 * palette rather than in hexes — `#666686` is `neutral[600]`, `#B3B3C9` is `neutral[400]`, `#EDEDF2` is
 * `neutral[100]`, `#DEF0FC`/`#AFDAF8` are `primaryColor[50]`/`[100]`, and so on. Nothing here is a new
 * colour.
 *
 * Everything is scoped by the caller to `down('md')`: a desktop keeps its tables.
 */

/** Anything a finger has to hit. The canvas's floor, and the one the old row menus broke at 20px. */
export const PHONE_TOUCH = 44;

/** The card's two-layer shadow: a hairline to seat it, a soft one to lift it. */
export const PHONE_CARD_SHADOW = '0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04)';

export const PHONE_CARD_RADIUS = '14px';

/** The surface every list row and every block on a record page sits on. */
export const phoneCardSx: SxProps<Theme> = {
    backgroundColor: theme.palette.common.white,
    border: '1px solid rgba(0, 0, 0, 0.05)',
    borderRadius: PHONE_CARD_RADIUS,
    boxShadow: PHONE_CARD_SHADOW,
    overflow: 'hidden',
};

/** Badge, identity, menu — the top line of a card. */
export const phoneCardHeadSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '14px 14px 10px',
};

/** The 36px square that says what kind of thing this is. */
export const phoneBadgeSx: SxProps<Theme> = {
    flexShrink: 0,
    width: 36,
    height: 36,
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.primary.main,
    background: `linear-gradient(135deg, ${theme.palette.primaryColor[50]} 0%, ${theme.palette.primaryColor[100]} 100%)`,
    '& svg': {
        fontSize: '19px',
    },
};

/** A badge for a person rather than a thing. */
export const phonePersonBadgeSx: SxProps<Theme> = {
    ...(phoneBadgeSx as object),
    color: theme.palette.secondary.main,
    background: `linear-gradient(135deg, ${theme.palette.secondaryColor[50]} 0%, ${theme.palette.secondaryColor[100]} 100%)`,
};

/** The name people search by. */
export const phoneTitleSx: SxProps<Theme> = {
    fontSize: '15px',
    fontWeight: 600,
    lineHeight: 1.25,
    color: theme.palette.text.primary,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
};

/**
 * A grid's own name cell, worn as a card's title.
 *
 * Three lists put their desktop name cell in the card head rather than re-implementing the link, so
 * that a row and its card cannot open different pages. That cell is built for a table row: a button
 * carrying a 32px avatar and a `subtitle2` label in link blue. In a card head the badge beside it
 * already says "a person", so the avatar only pushes the name off its line; and the identity line is
 * the darkest thing on the canvas, so a blue one makes the whole list read as a column of links.
 *
 * Presentation only - the cell keeps its own tap, which is what makes the destination identical to
 * the row's. What it deliberately no longer carries is the missing-Bexio warning, which used to ride
 * on this colour: a red name is not a message to somebody with no black ones beside it and no hover
 * to open a tooltip with. The card says it in words instead, with `MissingBexioPill`.
 */
export const phoneTitleCellSx: SxProps<Theme> = {
    minWidth: 0,
    '& .MuiButton-root': {
        minWidth: 0,
        maxWidth: '100%',
        padding: 0,
        gap: 0,
        justifyContent: 'flex-start',
        textAlign: 'left',
        '&:hover': {
            backgroundColor: 'transparent',
        },
    },
    '& .MuiButton-root > .MuiBox-root': {
        display: 'none',
    },
    '& .MuiTypography-root': {
        fontSize: '15px',
        fontWeight: 600,
        lineHeight: 1.25,
        color: theme.palette.text.primary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
};

/** The line that tells two similar records apart. */
export const phoneSubtitleSx: SxProps<Theme> = {
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: 1.35,
    color: theme.palette.neutral[600],
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
};

/** The row of pills under the identity: state first, then counts. They wrap; they never clip. */
export const phonePillsRowSx: SxProps<Theme> = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    padding: '0 14px 12px',
};

/**
 * The facts strip: at most four, two to a line.
 *
 * Label above value rather than beside it — a value that starts where the label ends cannot be read
 * down a column, which is the whole reason a table was readable in the first place.
 */
export const phoneFactsSx: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '2px 12px',
    padding: '10px 14px',
    borderTop: `1px solid ${theme.palette.neutral[100]}`,
    backgroundColor: '#FBFBFD',
};

export const phoneFactLabelSx: SxProps<Theme> = {
    fontSize: '10px',
    fontWeight: 500,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.palette.neutral[400],
};

export const phoneFactValueSx: SxProps<Theme> = {
    fontSize: '13px',
    fontWeight: 500,
    color: theme.palette.text.primary,
    fontVariantNumeric: 'tabular-nums',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
};

/** The card's own menu: a 44px target holding a 20px glyph. */
export const phoneMenuButtonSx: SxProps<Theme> = {
    width: PHONE_TOUCH,
    height: PHONE_TOUCH,
    flexShrink: 0,
    borderRadius: '10px',
    backgroundColor: 'transparent',
    '&:hover': {
        backgroundColor: theme.palette.neutral[50],
    },
    '& svg': {
        fontSize: '20px',
        fill: theme.palette.neutral[500],
    },
};

/** The bar at the top of a phone screen: menu, the page's name, its actions. */
export const phoneAppBarSx: SxProps<Theme> = {
    height: 56,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    padding: '0 8px 0 4px',
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.palette.neutral[100]}`,
};

/** The five places people go. Fixed to the bottom, above everything the page draws. */
export const phoneBottomBarSx: SxProps<Theme> = {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: theme.zIndex.appBar,
    display: 'grid',
    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
    backgroundColor: theme.palette.common.white,
    borderTop: `1px solid ${theme.palette.neutral[100]}`,
    padding: '6px 4px 10px',
};

/** One of its items. The active one is the only coloured thing in the bar. */
export const phoneBottomItemSx = (active: boolean) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    padding: '6px 2px',
    minHeight: PHONE_TOUCH,
    // The item may shrink to its share of the bar. Without this a grid item refuses to go narrower
    // than its content, which is how one long label pushed its own column out of true.
    minWidth: 0,
    textDecoration: 'none',
    color: active ? theme.palette.primary.main : theme.palette.neutral[500],
    '& svg': {
        fontSize: '22px',
    },
    /**
     * The label is held to the width of its column and centred in it.
     *
     * Centring by `align-items` alone only looks right while every label fits: a long one - "Charging
     * sessions" at 10px against a 74px column on a 390px screen - is wider than the item, overflows
     * it, and then reads as shifted off its own icon while the short ones beside it look centred.
     */
    '& .MuiTypography-root': {
        width: '100%',
        textAlign: 'center',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontSize: '10px',
        fontWeight: active ? 600 : 500,
        lineHeight: 1.1,
        color: 'inherit',
    },
});

/** How much room the fixed bottom bar takes from the page beneath it. */
export const PHONE_BOTTOM_BAR_HEIGHT = 72;

/** A sheet that rises from the bottom: row actions, filters, an export. */
export const phoneSheetSx: SxProps<Theme> = {
    '& .MuiPaper-root': {
        margin: 0,
        width: '100%',
        maxWidth: '100%',
        maxHeight: '88dvh',
        borderRadius: '20px 20px 0 0',
        boxShadow: '0 -8px 30px rgba(0,0,0,0.18)',
    },
};

/** The grab handle at the top of one. */
export const phoneSheetHandleSx: SxProps<Theme> = {
    width: 40,
    height: 4,
    borderRadius: '2px',
    backgroundColor: theme.palette.neutral[200],
    margin: '10px auto 6px',
};

/** One tappable line inside a sheet. */
export const phoneSheetItemSx = (destructive = false) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
    minHeight: 56,
    padding: '0 16px',
    justifyContent: 'flex-start',
    textTransform: 'none',
    borderRadius: 0,
    fontSize: '15px',
    fontWeight: 500,
    color: destructive ? theme.palette.brand.dark : theme.palette.text.primary,
    '& svg': {
        fontSize: '21px',
        color: destructive ? theme.palette.brand.dark : theme.palette.neutral[600],
    },
});

/**
 * The action bar pinned to the bottom of a record's page.
 *
 * Pinned because the primary action used to scroll away with the content, and on a phone the keyboard
 * hid whatever was left of it.
 */
export const phoneActionBarSx: SxProps<Theme> = {
    position: 'sticky',
    bottom: 0,
    zIndex: 2,
    display: 'flex',
    gap: '10px',
    padding: '10px 16px 22px',
    backgroundColor: theme.palette.common.white,
    borderTop: `1px solid ${theme.palette.neutral[100]}`,
};

export const phonePrimaryButtonSx: SxProps<Theme> = {
    flexGrow: 1,
    height: 48,
    borderRadius: '12px',
    textTransform: 'none',
    fontSize: '15px',
    fontWeight: 600,
    boxShadow: '0 4px 12px rgba(0, 127, 220, 0.3)',
    '&:hover': {
        boxShadow: '0 4px 12px rgba(0, 127, 220, 0.3)',
    },
};

export const phoneSecondaryButtonSx: SxProps<Theme> = {
    width: 48,
    height: 48,
    flexShrink: 0,
    borderRadius: '12px',
    border: `1px solid ${theme.palette.neutral[200]}`,
    backgroundColor: theme.palette.common.white,
    color: theme.palette.neutral[600],
};
